import { type } from 'arktype';
import type { WorkflowStepKind } from './workflow-definition.model';

// ─── Status enums ─────────────────────────────────────────────────────────────

export const workflowRunStatusSchema = type('"active" | "completed" | "failed" | "cancelled"').configure({
  description:
    'Lifecycle status of a workflow run. `active` — currently executing, `completed` — finished successfully, `failed` — terminated with an error, `cancelled` — terminated by user request.',
  examples: ['active', 'completed', 'failed', 'cancelled'],
});
export type WorkflowRunStatus = typeof workflowRunStatusSchema.infer;

export const workflowStepStatusSchema = type('"pending" | "active" | "processing" | "completed" | "skipped" | "failed"').configure({
  description:
    'Lifecycle status of an individual workflow step. `pending` — not yet started, `active` — awaiting user input, `processing` — backend work in flight, `completed` — finished successfully, `skipped` — intentionally bypassed, `failed` — terminated with an error.',
  examples: ['pending', 'active', 'processing', 'completed', 'skipped', 'failed'],
});
export type WorkflowStepStatus = typeof workflowStepStatusSchema.infer;

// ─── Request schemas ──────────────────────────────────────────────────────────

export const startWorkflowSchema = type({
  definitionKey: type('string').configure({
    description: 'Unique key of the workflow definition to start (see WorkflowDefinition.key).',
    examples: ['control.tenant-onboarding', 'app.shift-template-wizard'],
  }),
  'initialContext?': type('Record<string, unknown>').configure({
    description: 'Optional initial context object seeded into the workflow run (nullable when omitted).',
    examples: [{ tenantSlug: 'acme-ch' }, { planId: 'plan_01HXYZ123ABC' }],
  }),
}).configure({
  description: 'Request body for starting a new workflow run from a registered workflow definition.',
});
export type StartWorkflow = typeof startWorkflowSchema.infer;

export const submitStepSchema = type({
  payload: type('Record<string, unknown>').configure({
    description: 'Step-specific payload submitted by the user or producer; shape is defined by the step.',
    examples: [{ name: 'Acme AG', country: 'CH' }, { approved: true }],
  }),
}).configure({
  description: 'Request body for submitting the current active step of a workflow run.',
});
export type SubmitStep = typeof submitStepSchema.infer;

// ─── Response DTOs ────────────────────────────────────────────────────────────

export interface WorkflowRunResponse {
  id: string;
  definitionKey: string;
  definitionVersion: number;
  status: WorkflowRunStatus;
  currentStepKey: string | null;
  context: Record<string, unknown>;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowStepRunResponse {
  id: string;
  runId: string;
  stepKey: string;
  kind: WorkflowStepKind;
  status: WorkflowStepStatus;
  payload: Record<string, unknown> | null;
  result: Record<string, unknown> | null;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

// ─── SSE event ────────────────────────────────────────────────────────────────

export interface WorkflowProgressEvent {
  runId: string;
  stepKey: string;
  stepStatus: WorkflowStepStatus;
  runStatus: WorkflowRunStatus;
  context: Record<string, unknown>;
}
